import React, { Dispatch, SetStateAction, useState } from 'react';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import Toast from 'react-native-toast-message';
import { appAgentService } from '../../../services/central-api/app-agent';
import { UserNotInUnit } from '../../../services/central-api/base-service';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedActivity: ActivityAndService;
    onConfirm: () => void;
}

export default function DeleteActivitiesAndServicesModal({ selectedActivity, isModalVisible, setModalVisible, onConfirm }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    async function formSubmit() {
        try {
            setIsLoading(true);
            await appAgentService.deleteActivityAndService(selectedActivity.id);

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            onConfirm();
        } catch (err) {
            if (err instanceof UserNotInUnit) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('notActiveInUnit'),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
            closeModal();
        }
    }

    const closeModal = () => {
        setModalVisible(false);
    };

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => setModalVisible(false)}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <MyAppText style={styles.header}>{translate('attention')}</MyAppText>

                    <View style={{ flexDirection: 'column', rowGap: 4, alignItems: 'center' }}>
                        <MyAppText style={styles.subheader}>{translate('areYouSureYouWantToDeleteQuestion')}</MyAppText>
                        <MyAppText style={styles.subheader}>{translate('theActivityBelowWillBeDeleted')}:</MyAppText>
                        <MyAppText style={styles.subheaderLevelName}>
                            {selectedActivity.indicator.code}{' - '}
                            {selectedActivity.address}
                            {selectedActivity.number ? `, ${selectedActivity.number}` : ''}
                        </MyAppText>
                    </View>
                    <FormActions
                        viewStyle={{ flex: 1, justifyContent: 'space-around' }}
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                        buttonContentContainerStyle={{ flex: 1 }}
                        cancelText='goBack'
                        confirmText='delete'
                        onSubmit={formSubmit}
                        onClose={closeModal}
                        disabled={isLoading}
                        isLoading={isLoading}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 450,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32,
        alignItems: 'center'
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
        textAlign: 'center'
    },
    subheaderLevelName: {
        fontSize: 16,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
}));
